import React, { useState, useEffect, useContext } from "react";
import MainContainer from "../../components/MainContainer";
import { 
  makeStyles, 
  Paper, 
  Tabs, 
  Tab, 
  Box, 
  Typography,
  useTheme 
} from "@material-ui/core";
import {
  Settings as SettingsIcon,
  Schedule,
  Business,
  CreditCard,
  Help,
  Palette,
  CheckCircle,
  Email,
} from "@material-ui/icons";

import SchedulesForm from "../../components/SchedulesForm";
import CompaniesManager from "../../components/CompaniesManager";
import PlansManager from "../../components/PlansManager";
import HelpsManager from "../../components/HelpsManager";
import Options from "../../components/Settings/Options";
import Whitelabel from "../../components/Settings/Whitelabel";
import FinalizacaoAtendimento from "../../components/Settings/FinalizacaoAtendimento";

import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";

import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";

import useCompanySettings from "../../hooks/useSettings/companySettings";
import useSettings from "../../hooks/useSettings";
import ForbiddenPage from "../../components/ForbiddenPage";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  subtitle: {
    fontSize: "14px",
    color: "#666",
    marginTop: "4px",
  },
  mainPaper: {
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    overflow: "hidden",
  },
  tabsContainer: {
    borderBottom: "2px solid #e8ecf1",
    background: "#fafbfc",
    padding: "0 8px",
  },
  tab: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "14px",
    minHeight: "56px",
    padding: "12px 20px",
    color: "#666",
    transition: "all 0.3s ease",
    "&:hover": {
      color: "#1a1a1a",
      background: "rgba(59, 130, 246, 0.05)",
    },
    "&.Mui-selected": {
      color: "#3b82f6",
    },
  },
  tabIcon: {
    marginRight: "8px",
    fontSize: "20px",
  },
  contentPaper: {
    padding: "24px",
    minHeight: "calc(100vh - 300px)",
    background: "white",
  },
  root: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
}));

const SettingsCustom = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [tab, setTab] = useState("options");
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState({});
  const [oldSettings, setOldSettings] = useState({});
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);

  const { find, updateSchedules } = useCompanies();

  const { getAll: getAllSettings } = useCompanySettings();
  const { getAll: getAllSettingsOld } = useSettings();
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    async function findData() {
      if (!user || !user.companyId) {
        return;
      }

      setLoading(true);
      try {
        const companyId = user.companyId;

        const company = await find(companyId);
        const settingList = await getAllSettings(companyId);
        const settingListOld = await getAllSettingsOld();

        setCompany(company);
        setSchedules(company.schedules);
        setSettings(settingList);
        setOldSettings(settingListOld);
        setSchedulesEnabled(settingList.scheduleType === "company");
        setCurrentUser(user);
      } catch (e) {
        toast.error(e);
      }
      setLoading(false);
    }
    findData();
  }, []);

  useEffect(() => {
    if (!socket || !user || !user.companyId) return;
    const onSettingsEvent = () => {
      getAllSettingsOld().then(setOldSettings);
    };
    socket.on(`company-${user.companyId}-settings`, onSettingsEvent);
    return () => {
      socket.off(`company-${user.companyId}-settings`, onSettingsEvent);
    };
  }, [socket, user]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      setSchedules(data);
      await updateSchedules({ id: company.id, schedules: data });
      toast.success("Horários atualizados com sucesso.");
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  const isSuper = () => {
    return currentUser && currentUser.super;
  };

  return (
    <div className={classes.container}>
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          {/* Header */}
          <div className={classes.header}>
            <div>
              <h1 className={classes.title}>⚙️ {i18n.t("settings.title")}</h1>
              <p className={classes.subtitle}>
                Configure as opções e preferências do sistema
              </p>
            </div>
          </div>

          {/* Main Content */}
          <Paper className={classes.mainPaper} elevation={0}>
            <Box className={classes.tabsContainer}>
              <Tabs
                value={tab}
                indicatorColor="primary"
                textColor="primary"
                scrollButtons="auto"
                variant="scrollable"
                onChange={handleTabChange}
                TabIndicatorProps={{
                  style: {
                    height: "3px",
                    borderRadius: "3px 3px 0 0",
                  },
                }}
              >
                <Tab 
                  icon={<SettingsIcon className={classes.tabIcon} />}
                  label={i18n.t("settings.tabs.options")} 
                  value={"options"}
                  className={classes.tab}
                />
                {schedulesEnabled && (
                  <Tab 
                    icon={<Schedule className={classes.tabIcon} />}
                    label="Horários" 
                    value={"schedules"}
                    className={classes.tab}
                  />
                )}
                {user.profile === "admin" &&
                  user.finalizacaoComValorVendaAtiva && (
                    <Tab
                      icon={<CheckCircle className={classes.tabIcon} />}
                      label="Finalização do Atendimento"
                      value={"finalizacao"}
                      className={classes.tab}
                    />
                  )}
              </Tabs>
            </Box>

            <Box className={classes.contentPaper}>
              {tab === "schedules" && (
                <SchedulesForm
                  loading={loading}
                  onSubmit={handleSubmitSchedules}
                  initialValues={schedules}
                />
              )}

              {tab === "companies" && isSuper() && (
                <CompaniesManager />
              )}

              {tab === "plans" && isSuper() && (
                <PlansManager />
              )}

              {tab === "helps" && isSuper() && (
                <HelpsManager />
              )}

              {tab === "whitelabel" && isSuper() && (
                <Whitelabel settings={oldSettings} />
              )}

              {tab === "finalizacao" && (
                <FinalizacaoAtendimento
                  settings={settings}
                  onSettingsChange={(newSettings) => setSettings(newSettings)}
                />
              )}

              {tab === "options" && (
                <Options
                  settings={settings}
                  oldSettings={oldSettings}
                  user={currentUser}
                  scheduleTypeChanged={(value) =>
                    setSchedulesEnabled(value === "company")
                  }
                />
              )}
            </Box>
          </Paper>
        </>
      )}
    </div>
  );
};

export default SettingsCustom;